import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, UserPlus, X } from "lucide-react";

const ROLE_LABELS = {
  SUPER_ADMIN: "Super administrateur",
  ADMIN: "Administrateur",
  GESTIONNAIRE_CONTENU: "Gestionnaire de contenu",
  LECTURE_SEULE: "Lecture seule",
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("ADMIN");
  const [busy, setBusy] = useState(false);

  const load = () => api("/admin/utilisateurs").then(setAdmins).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/admin/utilisateurs", { method: "POST", body: { email, role } });
      toast.success(`Administrateur invité : ${email}`);
      setShowInvite(false);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const update = async (a, patch) => {
    try {
      await api(`/admin/utilisateurs/${a.id}`, {
        method: "PUT",
        body: { email: a.email, role: patch.role ?? a.role, actif: patch.actif ?? a.actif },
      });
      toast.success("Administrateur mis à jour");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const isSuper = user?.role === "SUPER_ADMIN";

  return (
    <AppLayout variant="admin">
      <div data-testid="admin-users-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Administrateurs</h1>
          {isSuper && (
            <button
              data-testid="invite-admin-button"
              onClick={() => setShowInvite(true)}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors"
            >
              <UserPlus className="h-4 w-4" /> Inviter un administrateur
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-users-table">
              <thead className="bg-[#F7F7F7] border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Email</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Rôle</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Statut</th>
                  {isSuper && <th className="text-right text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {!admins ? (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</td></tr>
                ) : admins.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-cfhtb-bg/50 transition-colors" data-testid={`admin-row-${a.email}`}>
                    <td className="px-4 py-3 text-sm font-medium text-cfhtb-heading">
                      <span className="inline-flex items-center gap-2">
                        <Shield className="h-4 w-4 text-cfhtb-light" /> {a.email}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2.5 py-0.5 rounded-full font-medium border bg-cfhtb-primary/10 text-cfhtb-primary border-cfhtb-primary/30">
                        {ROLE_LABELS[a.role] || a.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${
                        a.actif
                          ? "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30"
                          : "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30"
                      }`}>
                        {a.actif ? "Actif" : "Désactivé"}
                      </span>
                    </td>
                    {isSuper && (
                      <td className="px-4 py-3 text-right space-x-2">
                        <select
                          data-testid={`admin-role-select-${a.email}`}
                          value={a.role}
                          onChange={(e) => update(a, { role: e.target.value })}
                          className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                        >
                          {Object.keys(ROLE_LABELS).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                        <button
                          data-testid={`admin-toggle-${a.email}`}
                          onClick={() => update(a, { actif: !a.actif })}
                          className={`text-xs font-medium rounded-md px-3 py-1 border transition-colors ${
                            a.actif
                              ? "text-cfhtb-error border-cfhtb-error/40 hover:bg-cfhtb-error/5"
                              : "text-cfhtb-success border-cfhtb-success/40 hover:bg-cfhtb-success/5"
                          }`}
                        >
                          {a.actif ? "Désactiver" : "Réactiver"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-cfhtb-text/70">
          Le double facteur (TOTP) sera exigé pour les comptes administrateurs lors du lot sécurité.
          Le dernier SUPER_ADMIN actif ne peut pas être retiré.
        </p>
      </div>

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="invite-admin-modal">
          <form onSubmit={invite} className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Inviter un administrateur</h2>
              <button type="button" onClick={() => setShowInvite(false)} className="text-cfhtb-text hover:text-cfhtb-heading" data-testid="invite-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-cfhtb-heading mb-1.5">Email</label>
              <input type="email" required data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light" />
            </div>
            <div>
              <label className="block text-sm font-medium text-cfhtb-heading mb-1.5">Rôle</label>
              <select data-testid="invite-role-select" value={role} onChange={(e) => setRole(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
                {Object.keys(ROLE_LABELS).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <p className="text-xs text-cfhtb-text">
              L'administrateur se connectera par lien magique envoyé à cette adresse.
            </p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowInvite(false)}
                className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50">
                Annuler
              </button>
              <button type="submit" data-testid="invite-submit-button" disabled={busy}
                className="bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-60">
                {busy ? "Envoi…" : "Inviter"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AppLayout>
  );
}
